import {
  buildSeoPayload,
  createUrlResolver,
} from './seoMeta';

export {
  DEFAULT_META_DESCRIPTION,
  SITE_NAME,
  descriptionFromBody,
} from './seoMeta';

const MANAGED_ATTR = 'data-page-meta';
const JSON_LD_ID = 'page-meta-json-ld';

function getResolver() {
  const configured = process.env.REACT_APP_SITE_URL || '';
  const publicUrl = process.env.PUBLIC_URL || '';
  if (typeof window !== 'undefined' && !configured) {
    return createUrlResolver(window.location.origin, publicUrl);
  }
  return createUrlResolver(configured, publicUrl);
}

export function absoluteUrl(path) {
  return getResolver().absoluteUrl(path);
}

function clearManagedHeadTags() {
  document.querySelectorAll(`[${MANAGED_ATTR}]`).forEach((el) => el.remove());
}

function upsertManagedMeta({ name, property, content }) {
  let el = property
    ? document.querySelector(`meta[property="${property}"]`)
    : document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    if (property) el.setAttribute('property', property);
    else el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute(MANAGED_ATTR, 'true');
  el.setAttribute('content', content);
}

function upsertManagedLink(rel, href) {
  let el = document.querySelector(`link[${MANAGED_ATTR}][rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute(MANAGED_ATTR, 'true');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertJsonLd(data) {
  let el = document.getElementById(JSON_LD_ID);
  if (!el) {
    el = document.createElement('script');
    el.id = JSON_LD_ID;
    el.type = 'application/ld+json';
    el.setAttribute(MANAGED_ATTR, 'true');
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

/**
 * @param {string | Parameters<typeof buildSeoPayload>[1]} titleOrOptions
 * @param {string} [description]
 */
export function setPageMeta(titleOrOptions, description) {
  const resolver = getResolver();
  const options =
    typeof titleOrOptions === 'string'
      ? { title: titleOrOptions, description }
      : titleOrOptions;

  const seo = buildSeoPayload(resolver, options);

  clearManagedHeadTags();

  document.title = seo.title;

  if (seo.description) {
    upsertManagedMeta({ name: 'description', content: seo.description });
  }

  upsertManagedLink('canonical', seo.canonicalUrl);

  upsertManagedMeta({ property: 'og:title', content: seo.title });
  upsertManagedMeta({ property: 'og:description', content: seo.description });
  upsertManagedMeta({ property: 'og:type', content: seo.ogType });
  upsertManagedMeta({ property: 'og:url', content: seo.canonicalUrl });
  upsertManagedMeta({ property: 'og:site_name', content: 'Racontera' });
  upsertManagedMeta({ property: 'og:image', content: seo.shareImage });

  upsertManagedMeta({ name: 'twitter:card', content: 'summary_large_image' });
  upsertManagedMeta({ name: 'twitter:title', content: seo.title });
  upsertManagedMeta({ name: 'twitter:description', content: seo.description });
  upsertManagedMeta({ name: 'twitter:image', content: seo.shareImage });

  if (seo.ogType === 'article' && seo.datePublished) {
    upsertManagedMeta({ property: 'article:published_time', content: seo.datePublished });
    upsertManagedMeta({
      property: 'article:modified_time',
      content: seo.dateModified || seo.datePublished,
    });
    if (seo.author?.name && seo.author.type === 'Person') {
      upsertManagedMeta({ property: 'article:author', content: seo.author.name });
    }
  }

  if (seo.jsonLd) {
    upsertJsonLd(seo.jsonLd);
  }
}
